import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  TextField,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Collapse,
} from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Breadcrumbs, Link as MuiLink, useTheme, alpha } from "@mui/material";
import {
  useGetProgramQuery,
  useGetProgramTargetQuery,
  useCreateProgramTargetMutation,
  useDeleteProgramTargetMutation,
  useDuplicateProgramTargetMutation,
  useUpdateTargetSDMutation,
  useUpdateTargetStatusMutation,
  useReorderProgramTargetsMutation,
  useBulkTargetStatusMutation,
  useBulkTargetDeleteMutation,
  useGetProgramStudentsQuery,
  useGetProgramProgressQuery,
  useGetProgramHistoryQuery,
  useListPromptTemplatesQuery,
  useListMasteryWorkflowsQuery,
  useGetSessionDataQuery,
  useCloneProgramMutation,
} from "../../../Api/attoDeskApi";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { LegacyTokens, TypeScale, PageSurface, resolveStatusTriad, SeriesPalette, programStatusLabel, tint12, B13, B13Ink } from "../../../theme/b13Tokens";
import ImportTargetsDialog from "../../ProgramLibrary/shared/ImportTargetsDialog";
import ProgramLockedBanner from "../../ProgramLibrary/shared/ProgramLockedBanner";
import CustomizePromptLevelsModal from "../shared/CustomizePromptLevelsModal";
import StudentsStaffWorkspace from "../../ProgramLibrary/shared/StudentsStaffWorkspace";
import { Stack } from "@mui/material";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import { useStyles } from "../../../theme/appStyles";
import useIsMobile from "../../../Core/hook/useIsMobile";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SortIcon from "@mui/icons-material/Sort";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

interface ITarget {
  id: string;
  targetName: string;
  status: string;
  sdType?: string | null;
  sdText?: string | null;
  order?: number;
  // Batch 34  - 2.2  -  DB column names. `null` on these three means
  // "inherit from the program". Non-null means "target-specific override".
  promptTemplateId?: number | string | null;
  measurementType?: string | null;
  masteryWorkflowId?: number | string | null;
}

interface ISessionRecord {
  id: string;
  date: string;
  targetName: string;
  trials: number;
  correct: number;
  percent: number;
  promptLevel: string;
  staff: string;
}

const TARGET_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "waiting",      label: "Waiting" },
  { value: "probe",        label: "Probe" },
  { value: "acquisition",  label: "Acquisition" },
  { value: "mastered",     label: "Mastered" },
  { value: "closed",       label: "Closed" },
  { value: "hold",         label: "Hold" },
  { value: "discontinued", label: "Discontinued" },
];

// Program status set (used only where the program's own status is displayed,
// e.g. the Treatment tab header select).
const PROGRAM_STATUS_COLORS: Record<string, "default" | "primary" | "success" | "warning" | "error" | "info" | "secondary"> = {
  waiting:       "default",
  baseline:      "info",
  active:        "success",
  maintenance:   "warning",
  hold:          "secondary",
  closed:        "default",
  discontinued:  "error",
  mastered:      "primary",
};
const PROGRAM_STATUS_OPTIONS = ["waiting", "baseline", "active", "maintenance", "hold", "closed", "discontinued"];

const SD_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "",                        label: "None" },
  { value: "discriminative_stimulus", label: "Discriminative Stimulus (SD)" },
  { value: "instruction",             label: "Instruction (\"Try this\")" },
  { value: "prompt",                  label: "Prompt" },
];

const MEASUREMENT_OPTIONS = [
  "Percent Correct",
  "Task Analysis",
  "Frequency",
  "Duration",
  "Latency",
  "Interval",
  "ABC Behaviour",
];

// Enum keys the backend accepts for program_targets.measurement_type.
// The reconciler in ProgramTargetController writes both `measurement_type`
// (enum) and `measurementType` (label) — the frontend only sends the enum.
const TARGET_MEASUREMENT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "trial_percent_correct", label: "Percent Correct" },
  { value: "task_analysis",         label: "Task Analysis" },
  { value: "frequency",             label: "Frequency" },
  { value: "duration",              label: "Duration" },
  { value: "latency",               label: "Latency" },
  { value: "interval",              label: "Interval" },
  { value: "abc_behaviour",         label: "ABC Behaviour" },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

/**
 * Inline "Add Target" form. Renders as a single wide TableRow (colSpan
 * defaults to 6 to match the target table) containing a compact grid of
 * every field a target can be created with:
 *
 *   Target name (required, autofocus)
 *   Status (default Waiting)
 *   SD Type + SD text
 *   Prompt Levels / Measurement / Workflow — each defaulting to
 *   "Inherited from program" (payload NULL), which the backend treats as
 *   the inherit signal.
 *
 * Enter in the name field submits. Escape anywhere cancels. Save
 * disables while the mutation is in flight. The submit handler owns
 * calling refetchTargets() and toggling the row shut on success.
 */
type NewTargetSubmit = (payload: {
  targetName: string;
  status: string;
  sdType: string | null;
  sdText: string | null;
  promptTemplateId: number | string | null;
  measurement_type: string | null;
  masteryWorkflowId: number | string | null;
}) => Promise<boolean>;

const AddTargetFormBody: React.FC<{
  hierarchies: Array<{ id: number | string; templateName: string }>;
  workflows: Array<{ id: number | string; workflowName: string }>;
  onSubmit: NewTargetSubmit;
  onCancel: () => void;
}> = ({ hierarchies, workflows, onSubmit, onCancel }) => {
  const [targetName, setTargetName] = React.useState("");
  const [status, setStatus] = React.useState<string>("waiting");
  const [sdType, setSdType] = React.useState<string>("");
  const [sdText, setSdText] = React.useState<string>("");
  const [promptTemplateId, setPromptTemplateId] = React.useState<string>("");   // "" = inherit
  const [measurementType, setMeasurementType] = React.useState<string>("");     // "" = inherit
  const [masteryWorkflowId, setMasteryWorkflowId] = React.useState<string>(""); // "" = inherit
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!targetName.trim() || busy) return;
    setBusy(true);
    const ok = await onSubmit({
      targetName,
      status,
      sdType: sdType || null,
      sdText: sdText || null,
      promptTemplateId:  promptTemplateId  ? promptTemplateId  : null,
      measurement_type:  measurementType   ? measurementType   : null,
      masteryWorkflowId: masteryWorkflowId ? masteryWorkflowId : null,
    });
    setBusy(false);
    if (!ok) return;
  };

  return (
    <Box
      onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); if (!busy) onCancel(); } }}
      sx={{ backgroundColor: B13.canvas, p: 2, borderRadius: 1 }}
    >
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={8}>
            <TextField
              autoFocus fullWidth size="small" required label="Target name"
              value={targetName}
              onChange={(e) => setTargetName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              disabled={busy}
              helperText="Defaults: status Waiting; Prompt Levels / Measurement / Workflow inherited from the program."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy}>
                {TARGET_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>SD Type</InputLabel>
              <Select label="SD Type" value={sdType} onChange={(e) => setSdType(e.target.value)} disabled={busy}>
                {SD_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "none"} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth size="small" label="SD text (optional)"
              value={sdText} onChange={(e) => setSdText(e.target.value)} disabled={busy} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Prompt Levels</InputLabel>
              <Select label="Prompt Levels" value={promptTemplateId}
                onChange={(e) => setPromptTemplateId(String(e.target.value))} disabled={busy}>
                <MenuItem value=""><em>Inherited from program</em></MenuItem>
                {hierarchies.map((h) => (
                  <MenuItem key={h.id} value={String(h.id)}>{h.templateName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Measurement</InputLabel>
              <Select label="Measurement" value={measurementType}
                onChange={(e) => setMeasurementType(String(e.target.value))} disabled={busy}>
                <MenuItem value=""><em>Inherited from program</em></MenuItem>
                {TARGET_MEASUREMENT_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Mastery Workflow</InputLabel>
              <Select label="Mastery Workflow" value={masteryWorkflowId}
                onChange={(e) => setMasteryWorkflowId(String(e.target.value))} disabled={busy}>
                <MenuItem value=""><em>Inherited from program</em></MenuItem>
                {workflows.map((w) => (
                  <MenuItem key={w.id} value={String(w.id)}>{w.workflowName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onCancel} disabled={busy}>Cancel</Button>
              <Button variant="contained" onClick={submit} disabled={busy || !targetName.trim()}>
                {busy ? "Adding..." : "Add target"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
    </Box>
  );
};

/** TableRow wrapper for use inside the target table body. */
const AddTargetInlineRow: React.FC<{
  hierarchies: Array<{ id: number | string; templateName: string }>;
  workflows: Array<{ id: number | string; workflowName: string }>;
  onSubmit: NewTargetSubmit;
  onCancel: () => void;
  colSpan?: number;
}> = ({ colSpan = 6, ...rest }) => (
  <TableRow sx={{ "& td": { borderTop: "none", borderBottom: `1px dashed ${LegacyTokens.line}`, p: 0 } }}>
    <TableCell colSpan={colSpan}>
      <AddTargetFormBody {...rest} />
    </TableCell>
  </TableRow>
);

// Batch 36  - 3.5  -  one shared empty-state pattern: title, body, single action.
const EmptyPanel: React.FC<{ title: string; body?: string; actionLabel?: string; onAction?: () => void }> = ({
  title, body, actionLabel, onAction,
}) => (
  <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
    <Typography sx={{ ...TypeScale.sectionHead, mb: 0.5 }}>{title}</Typography>
    {body && (
      <Typography sx={{ ...TypeScale.caption, maxWidth: 460, mx: "auto", mb: actionLabel ? 2 : 0 }}>
        {body}
      </Typography>
    )}
    {actionLabel && onAction && (
      <Button variant="contained" size="small" onClick={onAction}>{actionLabel}</Button>
    )}
  </Box>
);

// Batch 37  - 1.5  -  the field-row treatment now pulls types + colours from the
// shared token set. Uppercase 11px tracked-out label sits above a comfortably
// larger 15px value; label/value contrast is what carries the "considered"
// feel. Used across Overview and Instructions so both panels agree on grammar.
const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ ...TypeScale.fieldValue, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
      {value ?? <Box component="span" sx={{ color: B13Ink.ink3 }}> - </Box>}
    </Typography>
  </Box>
);

// Batch 37  - 1.3  -  single status chip. Tinted background + dark text + saturated
// dot. Replaces every ad-hoc <Chip color="success"> etc. on this page.
const AbaStatusChip: React.FC<{ status: string | null | undefined; size?: "small" | "medium"; kind?: "program" | "target" }> = ({
  status, size = "small", kind = "target",
}) => {
  const t = resolveStatusTriad(status);
  // Batch 43  - 1  -  triad supplies bg/text/dot; the label comes from the
  // entity's own vocabulary. resolveStatusTriad aliases baseline/active/
  // maintenance to probe/acquisition/hold for palette, but the display
  // label for a program must not read "Acquisition".
  const label = kind === "program" ? programStatusLabel(status) : t.label;
  const sz = size === "medium"
    ? { fontSize: 13, py: 0.5, px: 1.25, dot: 8 }
    : { fontSize: 12, py: 0.25, px: 1, dot: 7 };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      backgroundColor: t.bg, color: t.text,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: sz.fontSize, fontWeight: 600, lineHeight: 1.2,
      px: sz.px, py: sz.py,
    }}>
      <Box component="span" sx={{
        width: sz.dot, height: sz.dot, borderRadius: "50%",
        backgroundColor: t.dot, flexShrink: 0,
      }} />
      {label}
    </Box>
  );
};

// Batch 37  - 3.6  -  tab label with a distinct count span. The count sits
// visibly separate from the label (space, not middot) and picks up ink3 so
// it reads as a count rather than as part of the word.
const TabLabel: React.FC<{ text: string; count?: number }> = ({ text, count }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}>
    {text}
    {typeof count === "number" && (
      <Box component="span" sx={{ color: B13Ink.ink3, fontSize: 12, fontWeight: 500 }}>{count}</Box>
    )}
  </Box>
);

// Batch 37  - 2.6  -  one summary-row figure. Number over an uppercase tracked-out
// label. No box, no card, no border between figures  -  pure typography carries
// the hierarchy. Numbers at 24/650 in ink; labels at 11 uppercase in ink3.
const SummaryFigure: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
  <Box>
    <Typography sx={{ fontSize: 24, fontWeight: 650, color: LegacyTokens.ink, lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mt: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showErrorMessage, showMessage } = useNotifier();
  const confirm = useConfirm();
  const isMobileRes = useIsMobile();

  const [activeTab, setActiveTab] = useState(0);
  // Batch 43  - 4  -  per-tab footer + Preview modal state. Cancel returns to
  // the library (no dirty state today  -  the View is read-only apart from
  // the target status Selects which persist immediately). Save routes to
  // the full editor since in-place editing is not wired here yet  -  this
  // matches the current Edit Program affordance in the header. Preview
  // opens the read-only "session-time" view.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  // Inline-expanding form visibility. Same state drives both the
  // ghost-button empty state and the toolbar-anchored button on the
  // populated table. Escape or Cancel collapses.
  const [addTargetOpen, setAddTargetOpen] = useState(false);
  // Batch 4A  -  kebab menu + its two items. targetsFilterOpen wired but
  // no filter UI rendered yet (per user turn  -  the filter is an open
  // client question; the toggle exists so the wiring is real, but the
  // reveal-panel is deliberately deferred).
  const [toolbarMenuAnchor, setToolbarMenuAnchor] = useState<HTMLElement | null>(null);
  const [targetsFilterOpen, setTargetsFilterOpen] = useState(false);
  // Batch 10  - 10.3  -  Customize prompt levels modal (visibility + order only).
  const [customizePromptsOpen, setCustomizePromptsOpen] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [expandedTargetId, setExpandedTargetId] = useState<string | null>(null);
  // Batch 46  -  target row overflow menu. Parent list stays exactly five
  // items (six with the copy divider). "Change measurement" opens a real
  // flyout anchored to its MenuItem, not an inline expand. "Change
  // target type" is disabled at the top level with a tooltip and never
  // opens. Menu placement flips above the anchor when the row sits low
  // in the viewport, so the menu never covers the target it acts on.
  const [targetMenuAnchor, setTargetMenuAnchor] = useState<null | HTMLElement>(null);
  const [targetMenuRow, setTargetMenuRow] = useState<ITarget | null>(null);
  const [measurementAnchor, setMeasurementAnchor] = useState<null | HTMLElement>(null);
  const [menuFlipUp, setMenuFlipUp] = useState(false);
  const openTargetMenu = (e: React.MouseEvent<HTMLElement>, target: ITarget) => {
    const btn = e.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    // Estimated menu height including divider + 6 items  310px; flip up
    // if the row would push the menu off the bottom of the viewport.
    const spaceBelow = window.innerHeight - rect.bottom;
    setMenuFlipUp(spaceBelow < 340);
    setTargetMenuAnchor(btn);
    setTargetMenuRow(target);
    setMeasurementAnchor(null);
  };
  const closeTargetMenu = () => {
    setTargetMenuAnchor(null);
    setTargetMenuRow(null);
    setMeasurementAnchor(null);
  };
  const openMeasurementFlyout = (e: React.MouseEvent<HTMLElement>) => {
    setMeasurementAnchor(e.currentTarget as HTMLElement);
  };
  const closeMeasurementFlyout = () => setMeasurementAnchor(null);
  // Batch 34  - 2.4  -  bulk-SD dialog state.
  const [bulkSdOpen, setBulkSdOpen] = useState(false);
  const [bulkSdType, setBulkSdType] = useState<string>("discriminative_stimulus");
  const [bulkSdText, setBulkSdText] = useState<string>("");

  // API hooks
  const { data: programData, isLoading: programLoading } = useGetProgramQuery(
    { id },
    { skip: !id }
  );
  const {
    data: targetData,
    isLoading: targetsLoading,
    refetch: refetchTargets,
  } = useGetProgramTargetQuery({ programId: id }, { skip: !id });

  const [createTarget] = useCreateProgramTargetMutation();
  const [deleteTarget] = useDeleteProgramTargetMutation();
  const [cloneProgram, { isLoading: markingAsTemplate }] = useCloneProgramMutation();
  const [duplicateTarget] = useDuplicateProgramTargetMutation();
  const [updateTargetSD] = useUpdateTargetSDMutation();
  const [updateTargetStatus] = useUpdateTargetStatusMutation();
  const [reorderTargets] = useReorderProgramTargetsMutation();
  const [bulkTargetStatus] = useBulkTargetStatusMutation();
  const [bulkTargetDelete] = useBulkTargetDeleteMutation();
  // Batch 34  - 2.2  -  lists used by the per-target override selects.
  const { data: hierarchyData } = useListPromptTemplatesQuery({ status: "active", perPage: 100 });
  const { data: workflowData }  = useListMasteryWorkflowsQuery({ status: "active", perPage: 100 });
  const hierarchies: Array<{ id: number|string; templateName: string }> = (Array.isArray(hierarchyData?.data) ? hierarchyData!.data : []) as any;
  const workflows:  Array<{ id: number|string; workflowName: string }>  = (Array.isArray(workflowData?.data)  ? workflowData!.data  : []) as any;

  // Batch 37  - 2.6 /  - 3.7  -  hoist student + session counts so the header
  // summary row and the tab labels can display them without each tab's
  // panel component owning its own fetch. The panels below re-use the same
  // hooks and share the RTK-Query cache  -  no extra request.
  const { data: studentsRaw } = useGetProgramStudentsQuery(id ? { programId: Number(id) } : {}, { skip: !id });
  const { data: sessionsRaw } = useGetSessionDataQuery(id ? { programId: Number(id) } : {}, { skip: !id });
  const studentsCount = Array.isArray(studentsRaw?.data) ? (studentsRaw!.data as any[]).length : null;
  const sessionsCount = Array.isArray(sessionsRaw?.data) ? (sessionsRaw!.data as any[]).length : null;

  // Batch 34  - 2.2  -  persist an override change on a single target. `null`
  // reverts to inherit-from-program. All three fields flow through
  // createTarget with an id (which the backend treats as an update).
  const persistOverride = async (target: ITarget, patch: Partial<Pick<ITarget, "promptTemplateId" | "measurementType" | "masteryWorkflowId">>) => {
    try {
      const res = await createTarget({
        id: target.id,
        programId: id,
        targetName: target.targetName,
        status: target.status,
        sdType: target.sdType ?? null,
        sdText: target.sdText ?? null,
        promptTemplateId:  patch.promptTemplateId  !== undefined ? patch.promptTemplateId  : (target.promptTemplateId  ?? null),
        measurementType:   patch.measurementType   !== undefined ? patch.measurementType   : (target.measurementType   ?? null),
        masteryWorkflowId: patch.masteryWorkflowId !== undefined ? patch.masteryWorkflowId : (target.masteryWorkflowId ?? null),
      }).unwrap();
      if (res.status) { showMessage("Target updated"); refetchTargets(); }
      else showErrorMessage(res.message || "Failed to update target");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to update target"); }
  };

  const persistSD = async (target: ITarget, patch: { sdType?: string|null; sdText?: string|null }) => {
    try {
      const res = await updateTargetSD({
        targetIds: [target.id],
        sdType: patch.sdType !== undefined ? patch.sdType : (target.sdType ?? null),
        sdText: patch.sdText !== undefined ? patch.sdText : (target.sdText ?? null),
      }).unwrap();
      if (res.status) { showMessage("SD updated"); refetchTargets(); }
      else showErrorMessage(res.message || "Failed to update SD");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to update SD"); }
  };

  // Batch 35A  - 3  -  /program/getAll returns `data` as an ARRAY even for
  // single-id lookups (see ProgramController::index). Prior code did
  // `programData?.data as any` and then read `.programName` off the array,
  // which is always undefined  -  hence the blank header + literal "Program"
  // breadcrumb. Normalise to the first (and only) row.
  const program = (Array.isArray(programData?.data)
    ? (programData!.data as any[])[0]
    : (programData?.data as any)) ?? null;
  // Locked-program gate. Derived from the payload this page already fetches
  // (backend TREE_PROJECTION_INCLUDED carries `isLocked` on every row and
  // coerces to bool). When the banner's Unlock mutation resolves, RTK
  // invalidates "program" → useGetProgramQuery here refetches → this flips
  // to false → all write affordances reappear without a reload.
  const programIsLocked: boolean = !!program?.isLocked;
  const targetsFromQuery: ITarget[] = Array.isArray(targetData?.data) ? (targetData!.data as ITarget[]) : [];

  // Optimistic-order override for drag-reorder. Populated the moment
  // the user drops a row; cleared once the invalidated query returns
  // with the persisted order. On mutation failure (e.g. 409 from the
  // lock guard) we clear the override AND toast the server message,
  // so the visual order snaps back to what the server has. See the
  // drag handler below for the rollback path.
  const [optimisticTargets, setOptimisticTargets] = useState<ITarget[] | null>(null);
  const targets: ITarget[] = optimisticTargets ?? targetsFromQuery;

  // Dnd sensors — Pointer 4-px activation avoids "click on the handle
  // was a drag" false-positives; KeyboardSensor gives Space=pick up,
  // Arrow=move, Space=drop, Escape=cancel for accessibility parity.
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onTargetDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = targets.findIndex((t) => String(t.id) === active.id);
    const to = targets.findIndex((t) => String(t.id) === over.id);
    if (from < 0 || to < 0) return;

    const reordered = arrayMove(targets, from, to);
    setOptimisticTargets(reordered); // optimistic paint
    const items = reordered.map((t, i) => ({ id: t.id, orderNumber: i + 1 }));
    try {
      const res: any = await reorderTargets({ items }).unwrap();
      if (res?.status) {
        // RTK invalidates programTarget tag → refetch fires → clear
        // override so the query result becomes canonical.
        refetchTargets();
        setOptimisticTargets(null);
      } else {
        // status:false without throw — rare, but treat as rollback.
        showErrorMessage(res?.message || "Failed to reorder targets");
        setOptimisticTargets(null);
      }
    } catch (e: any) {
      // Rollback on network / 409 / any error. Message surfaced via
      // e?.data?.message so the lock-guard's "Program is locked..."
      // reaches the user instead of a generic toast (same handler
      // shape as the seven target-write catch blocks).
      setOptimisticTargets(null);
      showErrorMessage(e?.data?.message || "Failed to reorder targets");
    }
  };

  // Target management
  //
  // Replaces the prior inline "type-a-name-and-hit-Enter" row. Sends the
  // full superset the backend validator accepts, so a user who wants to
  // set overrides at create time can — and a user who accepts every
  // inherited default still gets a target with status=waiting and NULL
  // overrides, exactly as before. Same hook, same endpoint.
  type NewTargetPayload = {
    targetName: string;
    status: string;
    sdType: string | null;
    sdText: string | null;
    promptTemplateId: number | string | null;
    measurement_type: string | null;
    masteryWorkflowId: number | string | null;
  };
  const handleAddTargetSubmit = async (payload: NewTargetPayload): Promise<boolean> => {
    if (!payload.targetName.trim() || !id) return false;
    try {
      const response = await createTarget({
        programId: id,
        targetName: payload.targetName.trim(),
        status: payload.status || "waiting",
        sdType: payload.sdType,
        sdText: payload.sdText,
        promptTemplateId:  payload.promptTemplateId,
        measurement_type:  payload.measurement_type,
        masteryWorkflowId: payload.masteryWorkflowId,
      } as any).unwrap();
      if (response.status) {
        showMessage("Target added");
        setAddTargetOpen(false);
        refetchTargets();
        return true;
      }
      showErrorMessage(response.message || "Failed to add target");
      return false;
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to add target");
      return false;
    }
  };

  const handleDeleteTarget = async (targetId: string) => {
    const ok = await confirm(confirmPresets.deletePermanent("target"));
    if (!ok) return;
    try {
      const response = await deleteTarget(targetId).unwrap();
      if (response.status) {
        showMessage("Target deleted");
        refetchTargets();
      } else {
        showErrorMessage(response.message || "Failed to delete target");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to delete target");
    }
  };

  // Batch 45  - 4  -  duplicate a single target. Backend picks the shortest
  // free (copy)/(copy N) suffix on this program; a 422 at N=99 surfaces
  // as an error toast telling the user to rename existing copies.
  const handleDuplicateTarget = async (targetId: string) => {
    try {
      const res: any = await duplicateTarget(targetId).unwrap();
      if (res?.status) {
        showMessage(res.message || "Target duplicated.");
        refetchTargets();
      } else {
        showErrorMessage(res?.message || "Failed to duplicate target.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to duplicate target.");
    }
  };

  // Batch 45  - 3  -  Target Settings menu item opens the same override
  // panel that the row chevron expands. Sets expandedTargetId so the
  // Collapse below the row opens, then scrolls the row into view.
  const handleOpenTargetSettings = (targetId: string) => {
    setExpandedTargetId(targetId);
    // Defer the scroll one tick so the Collapse has begun mounting.
    setTimeout(() => {
      const el = document.querySelector(`[data-target-row="${targetId}"]`);
      if (el && "scrollIntoView" in el) {
        (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  };

  // Batch 45  - 5  -  Copy support info to clipboard. Format:
  //   Program: <programName>
  //   Position: <orderNumber + 1>
  //   Target: <targetName>
  //   Status: <label>
  //   SD (...): <sdText>
  //   SD Type: <sdType>
  //   Measurement: <effective>
  //   Prompt Levels: <effective>
  //   Workflow: <effective>
  // Empty lines suppressed. Effective values resolve override vs
  // inherit-from-program at time of copy  -  matches what the session
  // recorder will actually use.
  const handleCopySupport = async (target: ITarget) => {
    const idx = targets.findIndex((t) => t.id === target.id);
    const position = idx >= 0 ? idx + 1 : (target as any).orderNumber ?? null;
    const effMeasurement = target.measurementType ?? program?.measurementType ?? null;
    const effPromptId = target.promptTemplateId ?? program?.promptTemplateId ?? null;
    const effWfId = target.masteryWorkflowId ?? program?.masteryWorkflowId ?? null;
    const promptName = effPromptId
      ? (hierarchies.find((h: any) => String(h.id) === String(effPromptId))?.templateName ?? null)
      : null;
    const workflowName = effWfId
      ? (workflows.find((w: any) => String(w.id) === String(effWfId))?.workflowName ?? null)
      : null;

    const lines: string[] = [];
    if (program?.programName) lines.push(`Program: ${program.programName}`);
    if (position != null) lines.push(`Position: ${position}`);
    lines.push(`Target: ${target.targetName ?? ""}`);
    if (target.status) lines.push(`Status: ${target.status}`);
    lines.push(""); // blank line between context block and clinical block
    if (target.sdText) lines.push(`SD (Discriminative Stimulus): ${target.sdText}`);
    if (target.sdType) lines.push(`SD Type: ${target.sdType}`);
    if (effMeasurement) lines.push(`Measurement: ${effMeasurement}`);
    if (promptName) lines.push(`Prompt Levels: ${promptName}`);
    if (workflowName) lines.push(`Workflow: ${workflowName}`);

    const text = lines.filter((l, i, arr) => !(l === "" && arr[i - 1] === "")).join("\n").trim();
    try {
      await navigator.clipboard.writeText(text);
      showMessage("Support info copied.");
    } catch {
      showErrorMessage("Clipboard blocked by browser. Copy manually.");
    }
  };


  const handleStatusChange = async (targetId: string, newStatus: string) => {
    try {
      // Backend expects `id` (see ProgramTargetController::updateStatus) and
      // one of the seven Batch 34  - 2.1 target-status values (lowercase).
      const response = await updateTargetStatus({
        id: targetId,
        status: String(newStatus).toLowerCase(),
      }).unwrap();
      if (response.status) {
        showMessage("Status updated");
        refetchTargets();
      } else {
        showErrorMessage(response.message || "Failed to update status");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to update status");
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTargets([]);
    } else {
      setSelectedTargets(targets.map((t) => t.id));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleTarget = (targetId: string) => {
    setSelectedTargets((prev) =>
      prev.includes(targetId) ? prev.filter((id) => id !== targetId) : [...prev, targetId]
    );
  };

  // Program status chip (Treatment tab header) uses PROGRAM_STATUS_COLORS.
  // Target-status chips route through resolveStatusTriad (theme/b13Tokens.ts)
  // — the same source AbaStatusChip uses — so no per-page target colour
  // helper exists.
  const getProgramStatusColor = (status: string) => PROGRAM_STATUS_COLORS[String(status ?? "").toLowerCase()] || "default";
  // Compat alias  -  existing program-status renderers used this name.
  const getStatusChipColor = getProgramStatusColor;

  // Batch 36  - 3.1  -  hex used for the header accent bar. Matches the chip
  // colour semantically but resolves to a concrete stroke so the border-left
  // can render it. Uses the current theme's palette.
  const getStatusAccentHex = (status: string): string => {
    const c = getProgramStatusColor(status);
    const map: Record<string, string> = {
      primary:   theme.palette.primary.main,
      success:   theme.palette.success.main,
      info:      theme.palette.info.main,
      warning:   theme.palette.warning.main,
      error:     theme.palette.error.main,
      secondary: theme.palette.secondary.main,
      default:   theme.palette.divider,
    };
    return map[c] ?? theme.palette.divider;
  };
  const targetStatusLabel = (raw: string) => {
    const v = String(raw ?? "").toLowerCase();
    const found = TARGET_STATUS_OPTIONS.find((o) => o.value === v);
    if (found) return found.label;
    // Legacy display: baseline ? Probe, active ? Acquisition.
    if (v === "baseline") return "Probe";
    if (v === "active") return "Acquisition";
    return raw || " - ";
  };

  if (programLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!program) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <Typography>Program not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3 } }}>
      {/* Breadcrumbs  -  Batch 32  - 1.3: ABA Therapy / Programs / <name> */}
      <Breadcrumbs sx={{ mb: 1, px: 1, pt: 1 }} separator="/">
        <MuiLink component={Link} to="/aba-therapy/programs" underline="hover" color="inherit">ABA Therapy</MuiLink>
        <MuiLink component={Link} to="/aba-therapy/programs" underline="hover" color="inherit">Programs</MuiLink>
        <Typography color="text.primary">{program.programName || "Program"}</Typography>
      </Breadcrumbs>

      {/* Locked-program banner. Sits above the header so the state is
         visible before any tab, control, or read-only surface. Unlock
         invalidates the "program" tag and this page auto-refetches;
         write affordances reappear without a reload. */}
      {programIsLocked && (
        <Box sx={{ px: 1 }}>
          <ProgramLockedBanner
            programId={program.id}
            programName={program.programName}
            disabledSurfaceLabel="editing this program's targets"
          />
        </Box>
      )}

      {/* Batch 37  - 2  -  header on the token system.  - 2.1 4px accent bar in
         the status dot colour;  - 2.2 title at pageTitle scale in --ink (the
         darkest, largest thing on the page);  - 2.3 chip inline;  - 2.4
         secondary line: category  -  folder;  - 2.5 flat tag pills;  - 2.6 the
         missing focal point  -  a summary row of four figures below the tags. */}
      {(() => {
        const triad = resolveStatusTriad(program.status);
        //  - 2.6  -  figures derived from data already loaded (no request added).
        const inProgress = targets.filter((t) => {
          const s = String(t.status ?? "").toLowerCase();
          return s === "acquisition" || s === "probe" || s === "active" || s === "baseline";
        }).length;
        return (
          <Card variant="outlined"
            sx={{
              mb: 3, overflow: "hidden",
              backgroundColor: LegacyTokens.surface,
              borderColor: LegacyTokens.line,
              borderRadius: `${LegacyTokens.radius.card}px`,
              borderLeft: `4px solid ${triad.dot}`,
              boxShadow: "none",
            }}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "flex-start" } }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                      {program.programName || <Box component="span" sx={{ fontStyle: "italic", color: B13Ink.ink3 }}>Untitled program</Box>}
                    </Typography>
                    {program.status && <AbaStatusChip status={program.status} size="medium" kind="program" />}
                  </Stack>
                  <Typography sx={{ ...TypeScale.caption }}>
                    {[program.treatmentAreaName || program.category || "Uncategorised", program.folderName || null].filter(Boolean).join("  -  ")}
                  </Typography>
                  {(() => {
                    // Batch 14 Step 3b  -  read from the pivot join
                    // (tagsWithColour) rather than the legacy JSON so
                    // rename + colour changes propagate. Keyed by
                    // tagId. Legacy JSON is the fallback path only.
                    type RichTag = { tagId: number; name: string; colour: string | null };
                    const richTags: RichTag[] =
                      Array.isArray((program as any).tagsWithColour) && (program as any).tagsWithColour.length > 0
                        ? (program as any).tagsWithColour
                        : (Array.isArray(program.tags)
                            ? (program.tags as string[]).map((t, i) => ({ tagId: -(i + 1), name: t, colour: null }))
                            : (typeof program.tags === "string" && program.tags.length
                                ? (() => { try { const p = JSON.parse(program.tags as unknown as string); return Array.isArray(p) ? p.map((t: string, i: number) => ({ tagId: -(i + 1), name: t, colour: null })) : []; } catch { return []; } })()
                                : []));
                    return richTags.length > 0 ? (
                      <Box sx={{ display: "flex", gap: 0.75, mt: 1.5, flexWrap: "wrap" }}>
                        {richTags.map((t) => (
                          <Box key={t.tagId} sx={{
                            fontSize: 12,
                            color: t.colour ?? B13Ink.ink3,
                            backgroundColor: t.colour ? tint12(t.colour) : B13.canvas,
                            borderRadius: `${LegacyTokens.radius.chip}px`,
                            px: 1, py: 0.25,
                          }}>{t.name}</Box>
                        ))}
                      </Box>
                    ) : null;
                  })()}
                </Box>
                <Box sx={{ flexShrink: 0, display: "flex", gap: 1, alignItems: "center" }}>
                  {/* Primary Edit — this is the sole Edit affordance on
                     the View page. The footer's Edit was dropped when
                     the two-affordances-for-one-action ambiguity was
                     resolved; see the footer comment for why the footer
                     no longer offers Edit. */}
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<EditSharpIcon fontSize="small" />}
                    onClick={() => navigate(`/aba-therapy/programs/${id}/edit`)}
                    sx={{
                      backgroundColor: B13.primary, color: LegacyTokens.surface,
                      textTransform: "none", fontWeight: 600,
                      px: 2, height: 40, borderRadius: `${LegacyTokens.radius.control}px`,
                      boxShadow: "none",
                      "&:hover": { backgroundColor: B13.primary, filter: "brightness(0.95)" },
                    }}
                  >
                    Edit Program
                  </Button>
                  {/* Batch 61 §5 — Save as template. This is a COPY
                     operation: `ProgramController::cloneProgram` with
                     asTemplate=true inserts a new row with is_template=1,
                     studentId=null. The original instance stays intact
                     for the student who owns it — converting in place
                     would orphan them. Label reads "Save as template"
                     (not "Mark as template") because the semantic is
                     copy-to-library, not a status change on this row. */}
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<LibraryAddOutlinedIcon fontSize="small" />}
                    disabled={markingAsTemplate || !id}
                    onClick={async () => {
                      if (!id || !program) return;
                      try {
                        const res: any = await cloneProgram({
                          programId: Number(id),
                          asTemplate: true,
                          newName: (program.programName ?? "Program") + " (Template)",
                        }).unwrap();
                        if (res?.status) {
                          const newId = (res.data as any)?.id;
                          showMessage("Template created. You're now viewing the new template.");
                          if (newId) navigate(`/aba-therapy/programs/${newId}`);
                        } else {
                          showErrorMessage(res?.message || "Could not create template.");
                        }
                      } catch {
                        showErrorMessage("Could not create template.");
                      }
                    }}
                    sx={{
                      color: B13.primary,
                      borderColor: tint12(B13.primary),
                      textTransform: "none", fontWeight: 600,
                      px: 2, height: 40, borderRadius: `${LegacyTokens.radius.control}px`,
                      "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
                    }}
                  >
                    {markingAsTemplate ? "Saving..." : "Save as template"}
                  </Button>
                </Box>
              </Box>

              {/*  - 2.6 summary row  -  no boxes, no cards, no borders between figures. */}
              <Box sx={{ display: "flex", gap: 4, mt: 3, flexWrap: "wrap" }} data-testid="program-header-summary">
                <SummaryFigure value={targets.length} label="Targets" />
                <SummaryFigure value={inProgress} label="In progress" />
                <SummaryFigure value={studentsCount ?? "-"} label={studentsCount === 1 ? "Student" : "Students"} />
                <SummaryFigure value={sessionsCount ?? "-"} label={sessionsCount === 1 ? "Session" : "Sessions"} />
              </Box>
            </Box>
          </Card>
        );
      })()}

      {/* Batch 37  - 3  -  no fill behind the tab row;  - 3.4 active tab gets a 2px
         indicator + ink text;  - 3.5 hover shifts text colour, no bg;  - 3.6 the
         count is a distinct span in ink3, separated by a space, no middot. */}
      <Card variant="outlined" sx={{
        overflow: "hidden", boxShadow: "none",
        backgroundColor: LegacyTokens.surface,
        borderColor: LegacyTokens.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
      }}>
        <Box sx={{ borderBottom: `1px solid ${LegacyTokens.line}`, px: 2 }}>
          <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} variant="scrollable" allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                textTransform: "none", fontSize: 14, fontWeight: 500,
                color: B13Ink.ink3, minHeight: 44,
                px: 2, py: 1.5,
                "&:hover": { color: B13Ink.ink2, backgroundColor: "transparent" },
              },
              "& .Mui-selected": { color: `${LegacyTokens.ink} !important`, fontWeight: 600 },
              "& .MuiTabs-indicator": { backgroundColor: B13.primary, height: 2 },
            }}>
            <Tab label="Overview" />
            <Tab label={<TabLabel text="Targets" count={targets.length} />} />
            <Tab label="Instructions" />
            <Tab label={<TabLabel text="Students" count={studentsCount ?? undefined} />} />
            <Tab label={<TabLabel text="Session Data" count={sessionsCount ?? undefined} />} />
            <Tab label="Progress" />
            <Tab label="History" />
          </Tabs>
        </Box>

        {/* Batch 37  - 5.1  -  Overview on the shared field grammar: 2-column
           grid, 32px gutter, 24px row gap, deliberate order (Measurement,
           Workflow, Category, Status) then Objective + Baseline Notes full
           width.  - 5.2 decision  -  status shown read-only here as the shared
           AbaStatusChip so it doesn't stand out as the one editable field
           among read-only values; the editable control lives in Edit
           Program. Report notes the decision. */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container columnSpacing={4} rowSpacing={3}>
              <Grid item xs={12} md={6}>
                <FieldRow label="Measurement Type" value={program.measurementType} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldRow label="Workflow" value={program.masteryWorkflowName} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldRow label="Prompt Levels" value={program.promptTemplateName} />
                {program.promptTemplateId && (
                  <Button
                    size="small"
                    onClick={() => setCustomizePromptsOpen(true)}
                    sx={{
                      mt: 0.5, textTransform: "none",
                      color: B13.primary, fontWeight: 600, fontSize: 13,
                    }}
                  >
                    Customize prompt levels
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldRow label="Treatment Area" value={program.treatmentAreaName ?? program.category} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldRow label="Status" value={program.status ? <AbaStatusChip status={program.status} size="medium" kind="program" /> : null} />
              </Grid>
              <Grid item xs={12}>
                <FieldRow label="Objective" value={program.objective} />
              </Grid>
              <Grid item xs={12}>
                <FieldRow label="Baseline Notes" value={program.baselineNotes} />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2 - Targets (Batch 36  - 2  -  Targets promoted to index 1) */}
        <TabPanel value={activeTab} index={1}>
          <Box p={2}>
            {/* Batch 34  - 2.4  -  bulk actions bar. Appears only when at least
               one target is selected. Never overlaps the top toolbar. */}
            {!programIsLocked && selectedTargets.length > 0 && (
              <Box data-testid="target-bulk-bar"
                sx={{
                  display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1,
                  border: "1px solid", borderColor: "primary.main", borderRadius: 1,
                  bgcolor: (t) => t.palette.action.hover,
                }}>
                <Typography variant="body2" sx={{ mr: 1 }} fontWeight={700}>
                  {selectedTargets.length} selected
                </Typography>
                <Button size="small" variant="outlined"
                  onClick={() => setBulkSdOpen(true)}>Set SD</Button>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Set Status</InputLabel>
                  <Select label="Set Status" value=""
                    onChange={async (e) => {
                      const v = String(e.target.value);
                      if (!v) return;
                      try {
                        const res = await bulkTargetStatus({ targetIds: selectedTargets, status: v }).unwrap();
                        if (res.status) { showMessage(res.message || "Status updated"); refetchTargets(); setSelectedTargets([]); setSelectAll(false); }
                        else showErrorMessage(res.message || "Failed to update status");
                      } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to update status"); }
                    }}>
                    {TARGET_STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" color="error" variant="outlined"
                  startIcon={<DeleteOutlineSharpIcon fontSize="small" />}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete ${selectedTargets.length} target(s)?`,
                      message: "The targets will be soft-deleted and can be restored from the database if needed.",
                      confirmText: "Delete",
                      variant: "danger",
                    });
                    if (!ok) return;
                    try {
                      const res = await bulkTargetDelete({ targetIds: selectedTargets }).unwrap();
                      if (res.status) { showMessage(res.message || "Deleted"); refetchTargets(); setSelectedTargets([]); setSelectAll(false); }
                      else showErrorMessage(res.message || "Failed to delete");
                    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to delete"); }
                  }}>
                  Delete
                </Button>
                <Button size="small" onClick={() => { setSelectedTargets([]); setSelectAll(false); }}>Clear</Button>
              </Box>
            )}

            {/* Toolbar  -  Batch 4A forks on target count.
               Empty: SUGGEST + IMPORT (solid purple) + kebab only.
                      No Select All, no ADD SD, no SORT.
               Populated: existing Select All + Import outlined (per
                          user "Populated: unchanged"). */}
            <Box
              sx={{
                display: "flex",
                justifyContent: targets.length === 0 ? "flex-end" : "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {targets.length > 0 && !programIsLocked && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selectAll}
                    indeterminate={
                      selectedTargets.length > 0 && selectedTargets.length < targets.length
                    }
                    onChange={handleSelectAll}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Select All
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {!programIsLocked && (targets.length === 0 ? (
                  <Button
                    size="small"
                    startIcon={<FileUploadIcon />}
                    variant="contained"
                    sx={{
                      backgroundColor: LegacyTokens.purple,
                      "&:hover": { backgroundColor: LegacyTokens.purple },
                      textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700,
                    }}
                    onClick={() => setImportDialogOpen(true)}
                  >
                    Import
                  </Button>
                ) : (
                  <Button
                    size="small"
                    startIcon={<FileUploadIcon />}
                    variant="outlined"
                    onClick={() => setImportDialogOpen(true)}
                  >
                    Import targets
                  </Button>
                ))}
                {/* Batch 4A kebab  -  exactly two items:
                     - Show target filter (Switch on the right; the row
                       does not close the menu on click; the filter
                       reveal is deferred pending client answer)
                     - Export targets to clipboard (plain MenuItem) */}
                <IconButton
                  size="small"
                  onClick={(e) => setToolbarMenuAnchor(e.currentTarget)}
                  aria-label="Target list actions"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={toolbarMenuAnchor}
                  open={Boolean(toolbarMenuAnchor)}
                  onClose={() => setToolbarMenuAnchor(null)}
                >
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 2, py: 0.75, minWidth: 240, cursor: "default",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Typography sx={{ fontSize: 14 }}>Show target filter</Typography>
                    <Checkbox
                      checked={targetsFilterOpen}
                      onChange={(e) => setTargetsFilterOpen(e.target.checked)}
                      size="small"
                    />
                  </Box>
                  <MenuItem
                    onClick={() => {
                      const text = targets.map((t) => t.targetName).join("\n");
                      navigator.clipboard.writeText(text).then(
                        () => showMessage(`Copied ${targets.length} target name${targets.length === 1 ? "" : "s"} to clipboard.`),
                        () => showErrorMessage("Copy failed."),
                      );
                      setToolbarMenuAnchor(null);
                    }}
                  >
                    Export targets to clipboard
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Batch 37  - 4  -  no dead space. Row anatomy left-to-right:
               checkbox 40  -  drag 32  -  # 40 right  -  name+SD (flex)  -  status
               140  -  actions 80. Name cell is two-line (name over SD text).
               3px left border in the status dot colour makes an 18-row list
               scannable  -  the eye reads the colour column before the text. */}
            <TableContainer>
              <Table size="small" sx={{ "& td, & th": { borderBottomColor: LegacyTokens.line } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: B13.canvas }}>
                    <TableCell padding="checkbox" sx={{ width: 40 }} />
                    <TableCell sx={{ width: 32, ...TypeScale.tableHeader }} />
                    <TableCell sx={{ width: 40, textAlign: "right", ...TypeScale.tableHeader }}>#</TableCell>
                    <TableCell sx={{ ...TypeScale.tableHeader }}>Target</TableCell>
                    <TableCell sx={{ width: 140, ...TypeScale.tableHeader }}>Status</TableCell>
                    <TableCell sx={{ width: 80, textAlign: "right", ...TypeScale.tableHeader }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Batch 4A  -  the add-target affordance forks on target
                     count. EMPTY (no targets): a full-width outlined ghost
                     ADD TARGET button is the entire empty state  -  no top
                     type-and-Enter row, no "No targets yet" text, no
                     illustration. Clicking the ghost reveals the type-and-
                     Enter input for the first target. POPULATED: the
                     top-anchored type-and-Enter row from the prior turn.
                     Reconciled per user turn  -  two competing add
                     affordances would be worse than one that switches
                     shape by state. */}
                  {/* Populated-state add affordance: a compact ghost row
                     showing an ADD TARGET button. Clicking swaps in the
                     inline form (AddTargetInlineRow) as a full-width row
                     directly beneath. Same visual language as the empty
                     state; the two paths differ only in what surrounds
                     them. */}
                  {!programIsLocked && !targetsLoading && targets.length > 0 && !addTargetOpen && (
                    <TableRow sx={{
                      backgroundColor: B13.canvas,
                      "& td": {
                        borderTop: "none",
                        borderBottom: `1px dashed ${LegacyTokens.line}`,
                      },
                    }}>
                      <TableCell colSpan={6} sx={{ py: 1 }}>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => setAddTargetOpen(true)}
                          sx={{
                            border: `1.5px solid ${B13.primary}`,
                            color: B13.primary,
                            backgroundColor: "transparent",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            fontWeight: 700,
                            "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
                          }}
                        >
                          Add target
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {!programIsLocked && addTargetOpen && targets.length > 0 && (
                    <AddTargetInlineRow
                      hierarchies={hierarchies}
                      workflows={workflows}
                      onSubmit={handleAddTargetSubmit}
                      onCancel={() => setAddTargetOpen(false)}
                    />
                  )}
                  {targetsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : targets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 2, borderBottom: "none" }}>
                        {programIsLocked ? (
                          <Typography sx={{ ...TypeScale.tableCell, textAlign: "center", py: 3, color: LegacyTokens.ink3 }}>
                            No targets on this program.
                          </Typography>
                        ) : addTargetOpen ? (
                          <AddTargetFormBody
                            hierarchies={hierarchies}
                            workflows={workflows}
                            onSubmit={handleAddTargetSubmit}
                            onCancel={() => setAddTargetOpen(false)}
                          />
                        ) : (
                          <Button
                            fullWidth
                            startIcon={<AddIcon />}
                            onClick={() => setAddTargetOpen(true)}
                            sx={{
                              py: 1.25,
                              border: `1.5px solid ${B13.primary}`,
                              color: B13.primary,
                              backgroundColor: "transparent",
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              fontWeight: 700,
                              "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
                            }}
                          >
                            Add target
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <SortableTargetsBody
                      targets={targets}
                      sensors={dragSensors}
                      onDragEnd={onTargetDragEnd}
                    >
                    {targets.map((target, index) => {
                      const triad = resolveStatusTriad(target.status);
                      const expanded = expandedTargetId === target.id;
                      return (
                      <SortableTargetRow
                        key={target.id}
                        id={String(target.id)}
                        triadDot={triad.dot}
                      >
                      {({ handleProps, handleRef, nodeRef, rowStyle, isDragging }) => (
                      <React.Fragment>
                        <TableRow ref={nodeRef} style={rowStyle} data-target-row={target.id} sx={{
                          height: 56,
                          //  - 4.3 3px accent strip in the status dot colour on the leading cell.
                          "& > td:first-of-type": { boxShadow: `inset 3px 0 0 0 ${triad.dot}` },
                          "&:hover": { backgroundColor: (t) => t.palette.action.hover },
                          // Table drag idiom — no card elevation. A
                          // subtle background shift + slight opacity
                          // marks the row as in flight without lifting
                          // it out of the table's visual rhythm.
                          ...(isDragging && {
                            backgroundColor: (t) => t.palette.action.selected,
                            opacity: 0.75,
                          }),
                        }}>
                          <TableCell padding="checkbox">
                            {!programIsLocked && (
                              <Checkbox
                                size="small"
                                checked={selectedTargets.includes(target.id)}
                                onChange={() => handleToggleTarget(target.id)}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ width: 32 }}>
                            {/* Handle absent when program is locked — reorder
                               is a write, and a disabled drag handle still
                               reads as a drag handle. */}
                            {!programIsLocked && (
                              <Box
                                ref={handleRef}
                                {...handleProps}
                                aria-label={`Reorder target ${index + 1}`}
                                sx={{
                                  display: "inline-flex", alignItems: "center",
                                  cursor: isDragging ? "grabbing" : "grab",
                                  color: B13Ink.ink3,
                                  touchAction: "none",
                                  "&:hover": { color: B13.primary },
                                }}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ width: 40, textAlign: "right", ...TypeScale.tableCell, color: B13Ink.ink3 }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ cursor: "pointer" }}
                            onClick={() => setExpandedTargetId(expanded ? null : target.id)}>
                            {/* Batch 47  - 2  -  SD reads first (the technician
                               reads it aloud at session time). Small blue
                               "SD" badge, then either the SD text in quotes
                               or the SD Type label if no text is set. Target
                               name sits below on its own line. Chevron on
                               the right of the SD line expands the panel. */}
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                  <Box component="span" sx={{
                                    display: "inline-flex", alignItems: "center",
                                    backgroundColor: LegacyTokens.blueSoft,
                                    color: LegacyTokens.blueText,
                                    fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                                    px: 0.75, py: 0.15,
                                    borderRadius: `${LegacyTokens.radius.chip}px`,
                                    flexShrink: 0,
                                  }}>SD</Box>
                                  <Typography sx={{
                                    fontSize: 13.5, color: LegacyTokens.ink,
                                    fontWeight: target.sdText ? 500 : 400,
                                    fontStyle: target.sdText ? "normal" : "italic",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    flex: 1, minWidth: 0,
                                  }}>
                                    {target.sdText
                                      ? `"${target.sdText}"`
                                      : (target.sdType || "Discriminative Stimulus (SD)")}
                                  </Typography>
                                </Box>
                                <Typography sx={{
                                  fontSize: 13, color: B13Ink.ink2, fontWeight: 500,
                                  mt: 0.5,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                  {target.targetName}
                                </Typography>
                              </Box>
                              <Box sx={{ mt: -0.25, flexShrink: 0 }}>
                                {expanded
                                  ? <ExpandMoreIcon fontSize="small" sx={{ color: B13Ink.ink3 }} />
                                  : <ChevronRightIcon fontSize="small" sx={{ color: B13Ink.ink3 }} />}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: 140 }}>
                            <AbaStatusChip status={target.status} />
                          </TableCell>
                          <TableCell sx={{ width: 56, textAlign: "right" }}>
                            {/* Kebab absent when locked — every item in the
                               menu is a write (edit / duplicate / delete /
                               status change). A disabled kebab is chrome
                               for chrome's sake. */}
                            {!programIsLocked && (
                              <IconButton size="small"
                                aria-label="Target actions"
                                onClick={(e) => openTargetMenu(e, target)}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expanded row details */}
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{ py: 0, border: expandedTargetId === target.id ? undefined : 0 }}
                          >
                            <Collapse
                              in={expandedTargetId === target.id}
                              timeout="auto"
                              unmountOnExit
                            >
                              {/* Native fieldset cascades disabled to all
                                 Select / Button / TextField descendants —
                                 same pattern as the field editor. Locked
                                 programs still SHOW the expanded settings
                                 so the user can read them; they just
                                 cannot change them. */}
                              <fieldset
                                disabled={programIsLocked}
                                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
                              >
                              <Box sx={{ py: 2.5, px: 4, bgcolor: (t) => t.palette.action.hover, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}
                                  sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 2 }}>
                                  Target-Specific Settings
                                </Typography>
                                {/* Batch 46  - 4  -  grouped layout. Row 1: three
                                   inheritance selects (Prompt Levels /
                                   Measurement / Workflow)  -  the
                                   override axis. Row 2: Status + SD Type  -  the
                                   session-recorder axis. Row 3: SD text full
                                   width. 24px between groups (mb: 3), 16px
                                   within (Grid spacing 2). */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                  <Grid item lg={4} md={4} sm={12} xs={12}>
                                    {(() => {
                                      const overriding = target.promptTemplateId != null && target.promptTemplateId !== "";
                                      const inheritedId = program?.promptTemplateId ?? null;
                                      const inheritedName = hierarchies.find((h) => String(h.id) === String(inheritedId))?.templateName ?? "(none set on program)";
                                      return (
                                        <>
                                          <FormControl fullWidth size="small">
                                            <InputLabel>Prompt Levels</InputLabel>
                                            <Select
                                              label="Prompt Levels"
                                              value={overriding ? String(target.promptTemplateId) : ""}
                                              onChange={(e) => persistOverride(target, { promptTemplateId: e.target.value === "" ? null : (e.target.value as any) })}
                                            >
                                              <MenuItem value=""><em>Inherit from program ({inheritedName})</em></MenuItem>
                                              {hierarchies.map((h) => (
                                                <MenuItem key={h.id} value={String(h.id)}>{h.templateName}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                            <Chip size="small" variant="outlined"
                                              color={overriding ? "warning" : "default"}
                                              label={overriding ? "Override" : "Inherited"} />
                                            {overriding && (
                                              <Button size="small" onClick={() => persistOverride(target, { promptTemplateId: null })}>
                                                Revert to inherited
                                              </Button>
                                            )}
                                          </Box>
                                        </>
                                      );
                                    })()}
                                  </Grid>

                                  <Grid item lg={4} md={4} sm={12} xs={12}>
                                    {(() => {
                                      const overriding = !!target.measurementType;
                                      const inherited = program?.measurementType ?? "(program default)";
                                      return (
                                        <>
                                          <FormControl fullWidth size="small">
                                            <InputLabel>Measurement</InputLabel>
                                            <Select
                                              label="Measurement"
                                              value={overriding ? String(target.measurementType) : ""}
                                              onChange={(e) => persistOverride(target, { measurementType: e.target.value === "" ? null : String(e.target.value) })}
                                            >
                                              <MenuItem value=""><em>Inherit from program ({inherited})</em></MenuItem>
                                              {MEASUREMENT_OPTIONS.map((m) => (
                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                            <Chip size="small" variant="outlined"
                                              color={overriding ? "warning" : "default"}
                                              label={overriding ? "Override" : "Inherited"} />
                                            {overriding && (
                                              <Button size="small" onClick={() => persistOverride(target, { measurementType: null })}>
                                                Revert to inherited
                                              </Button>
                                            )}
                                          </Box>
                                        </>
                                      );
                                    })()}
                                  </Grid>

                                  <Grid item lg={4} md={4} sm={12} xs={12}>
                                    {(() => {
                                      const overriding = target.masteryWorkflowId != null && target.masteryWorkflowId !== "";
                                      const inheritedId = program?.masteryWorkflowId ?? null;
                                      const inheritedName = workflows.find((w) => String(w.id) === String(inheritedId))?.workflowName ?? "(none set on program)";
                                      return (
                                        <>
                                          <FormControl fullWidth size="small">
                                            <InputLabel>Workflow</InputLabel>
                                            <Select
                                              label="Workflow"
                                              value={overriding ? String(target.masteryWorkflowId) : ""}
                                              onChange={(e) => persistOverride(target, { masteryWorkflowId: e.target.value === "" ? null : (e.target.value as any) })}
                                            >
                                              <MenuItem value=""><em>Inherit from program ({inheritedName})</em></MenuItem>
                                              {workflows.map((w) => (
                                                <MenuItem key={w.id} value={String(w.id)}>{w.workflowName}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                            <Chip size="small" variant="outlined"
                                              color={overriding ? "warning" : "default"}
                                              label={overriding ? "Override" : "Inherited"} />
                                            {overriding && (
                                              <Button size="small" onClick={() => persistOverride(target, { masteryWorkflowId: null })}>
                                                Revert to inherited
                                              </Button>
                                            )}
                                          </Box>
                                        </>
                                      );
                                    })()}
                                  </Grid>

                                </Grid>
                                {/* Row 2  -  session-recorder axis: Status + SD Type. */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                  <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Status</InputLabel>
                                      <Select
                                        label="Status"
                                        value={String(target.status ?? "waiting").toLowerCase()}
                                        onChange={(e) => handleStatusChange(target.id, String(e.target.value))}
                                      >
                                        {TARGET_STATUS_OPTIONS.map((o) => {
                                          // B13 §B.1 tinted-fill anatomy, same source
                                          // AbaStatusChip uses. Select has no
                                          // renderValue prop, so this chip renders
                                          // both inside each MenuItem AND as the
                                          // closed-state display of the selected
                                          // value — one edit covers both.
                                          const triad = resolveStatusTriad(o.value);
                                          return (
                                            <MenuItem key={o.value} value={o.value}>
                                              <Chip size="small"
                                                label={o.label}
                                                sx={{
                                                  mr: 1,
                                                  backgroundColor: triad.bg,
                                                  color: triad.text,
                                                  border: `1px solid ${triad.text}`,
                                                  fontWeight: 600,
                                                  "& .MuiChip-label": { color: triad.text },
                                                }}
                                              />
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  {/* Batch 34  - 2.3  -  SD (Discriminative Stimulus). sdType
                                     categorises; sdText holds the content. */}
                                  <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>SD Type</InputLabel>
                                      <Select
                                        label="SD Type"
                                        value={String(target.sdType ?? "")}
                                        onChange={(e) => persistSD(target, { sdType: e.target.value === "" ? null : String(e.target.value) })}
                                      >
                                        {SD_TYPE_OPTIONS.map((o) => (
                                          <MenuItem key={o.value || "none"} value={o.value}>{o.label}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                                {/* Row 3  -  SD text full width. Batch 46  - 5:
                                   label is the field name only; the "what the
                                   staff member says or presents" gloss is
                                   moved to helper text where it belongs. */}
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <TextField
                                      size="small" fullWidth
                                      label="Discriminative Stimulus (SD)"
                                      helperText="What the staff member says or presents at the start of the trial."
                                      onBlur={(e) => {
                                        if ((e.target.value ?? "") !== (target.sdText ?? "")) {
                                          persistSD(target, { sdText: e.target.value || null });
                                        }
                                      }}
                                      defaultValue={target.sdText ?? ""}
                                      key={`sdText-${target.id}-${target.sdText ?? ""}`}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                              </fieldset>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                      )}
                      </SortableTargetRow>
                      );
                    })}
                    </SortableTargetsBody>
                  )}

                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 3  -  Instructions (Batch 36  - 1.2: renders program teaching
           fields with a proper empty state when none are populated. The
           old panel here mistakenly showed a target-SD list.) */}
        <TabPanel value={activeTab} index={2}>
          {/* Batch 3A  -  merge-token banner. BANNER ONLY: no replacement
             logic anywhere, no token engine, no live preview. The full
             shortcut list is not known and shipping two tokens out of
             an unknown-length list is worse than shipping none. See
             the Batch 3A audit answers in the release notes. "Help
             section" renders as plain bold text because no /help route
             exists in the FE routing table. */}
          <Box
            sx={{
              mx: { xs: 2, md: 3 }, mt: { xs: 2, md: 3 }, mb: 2,
              p: 1.75,
              border: `1px solid ${LegacyTokens.emerald}`,
              borderRadius: `${LegacyTokens.radius.card}px`,
              backgroundColor: alpha(LegacyTokens.emerald, 0.06),
              display: "flex", alignItems: "flex-start", gap: 1.25,
            }}
          >
            <Typography sx={{
              fontSize: 12, fontWeight: 800, letterSpacing: "0.08em",
              textTransform: "uppercase", color: LegacyTokens.emerald,
              lineHeight: 1.7, flexShrink: 0,
            }}>
              Tip
            </Typography>
            <Typography sx={{ fontSize: 13, color: B13Ink.ink2, lineHeight: 1.55 }}>
              Save time by using shortcuts tags when writing programs.{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>Learner</Box> will
              automatically be replaced by the learners name.{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>He/she</Box> will
              be replaced by the correct personal pronoun according to the
              learners gender. See the full list of replacement shortcuts in
              our <Box component="span" sx={{ fontWeight: 700 }}>Help section</Box>.
            </Typography>
          </Box>
          {(() => {
            const fields: Array<{ key: string; label: string; multi?: boolean }> = [
              { key: "operational_definition",      label: "Operational Definition" },
              { key: "teaching_procedure",          label: "Teaching Procedure", multi: true },
              { key: "sd_general",                  label: "Discriminative Stimulus (SD)" },
              { key: "expected_response",           label: "Expected Response" },
              { key: "incorrect_response_handling", label: "Incorrect-Response Handling" },
              { key: "error_correction",            label: "Error Correction" },
              { key: "reinforcement",               label: "Reinforcement" },
              { key: "materials",                   label: "Materials" },
              { key: "generalization_notes",        label: "Generalization Notes" },
              { key: "maintenance_notes",           label: "Maintenance Notes" },
              { key: "safety_notes",                label: "Safety Notes" },
              { key: "additional_notes",            label: "Additional Notes" },
            ];
            const filled = fields.filter((f) => program[f.key] && String(program[f.key]).trim() !== "");
            if (filled.length === 0) {
              return (
                <EmptyPanel
                  title="No teaching instructions yet."
                  body="Add the teaching procedure, expected response, error-correction plan, and materials in the program editor."
                  actionLabel="Edit Program"
                  onAction={() => navigate(`/aba-therapy/programs/${id}/edit`)}
                />
              );
            }
            return (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container columnSpacing={4} rowSpacing={3}>
                  {filled.map((f) => {
                    const raw = String(program[f.key] ?? "");
                    // Batch 37  - 5.3  -  teaching-procedure numbered list gets a
                    // proper hanging indent: numbers align in a first column,
                    // wrapped text hangs under the text column. Any field
                    // whose lines all match `^\d+\.\s+` gets the treatment.
                    const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
                    const numbered = lines.length > 1 && lines.every((l) => /^\d+\.\s+/.test(l));
                    return (
                      <Grid key={f.key} item xs={12} md={f.multi ? 12 : 6}>
                        {numbered ? (
                          <Box>
                            <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>{f.label}</Typography>
                            <Box sx={{
                              display: "grid",
                              gridTemplateColumns: "auto 1fr",
                              columnGap: 1.5, rowGap: 0.75,
                            }}>
                              {lines.map((l, i) => {
                                const m = l.match(/^(\d+\.)\s+(.*)$/);
                                return (
                                  <React.Fragment key={i}>
                                    <Box sx={{ ...TypeScale.fieldValue, color: B13Ink.ink3, fontVariantNumeric: "tabular-nums" }}>
                                      {m ? m[1] : ""}
                                    </Box>
                                    <Box sx={{ ...TypeScale.fieldValue, lineHeight: 1.5 }}>
                                      {m ? m[2] : l}
                                    </Box>
                                  </React.Fragment>
                                );
                              })}
                            </Box>
                          </Box>
                        ) : (
                          <FieldRow label={f.label} value={program[f.key]} />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })()}
        </TabPanel>

        {/* Tab 4  -  Students (Batch 32 real data via /program/students) */}
        <TabPanel value={activeTab} index={3}>
          <ProgramStudentsPanel
            programId={id}
            hierarchies={hierarchies}
            workflows={workflows}
            targets={targets}
          />
        </TabPanel>

        {/* Tab 5  -  Session Data (Batch 36  - 1.1: developer-scaffolding text
           removed. Renders a proper empty state until session records exist;
           the chart itself will be wired via <ProgramProgressChart> in a
           follow-up once the demo dataset is seeded  -  highcharts is already
           a dependency, no new lib needed.) */}
        <TabPanel value={activeTab} index={4}>
          <ProgramSessionDataPanel
            programId={id}
            onRecord={() => navigate(`/aba-therapy/sessions/new?programId=${id}`)}
          />
        </TabPanel>

        {/* Tab 6  -  Progress (Batch 32 real SVG accuracy chart) */}
        <TabPanel value={activeTab} index={5}>
          <ProgramProgressPanel programId={id} />
        </TabPanel>

        {/* Tab 7  -  History (Batch 32 assembled from real timestamps) */}
        <TabPanel value={activeTab} index={6}>
          <ProgramHistoryPanel programId={id} />
        </TabPanel>

        {/* Batch 43  - 4  -  per-tab footer. Renders on every tab (outside
           the TabPanels but inside the tab card). Preview / Cancel / Save
           parity with the client's tool. Preview opens the session-time
           read-only modal. Save routes to the full editor since in-place
           editing is not yet wired on this page. */}
        <Box sx={{
          borderTop: `1px solid ${LegacyTokens.line}`,
          backgroundColor: B13.canvas,
          px: { xs: 2, md: 3 }, py: 1.5,
          display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1,
        }}>
          <Button
            size="medium"
            startIcon={<VisibilityIcon fontSize="small" />}
            onClick={() => setPreviewOpen(true)}
            sx={{
              color: B13.primary, textTransform: "none", fontWeight: 600,
              border: `1px solid ${tint12(B13.primary)}`,
              px: 2, height: 40, borderRadius: `${LegacyTokens.radius.control}px`,
              "&:hover": { backgroundColor: tint12(B13.primary) },
            }}
          >Preview</Button>
          <Button
            size="medium"
            onClick={() => navigate("/aba-therapy/programs")}
            sx={{
              color: B13Ink.ink3, textTransform: "none", fontWeight: 500,
              px: 2, height: 40, borderRadius: `${LegacyTokens.radius.control}px`,
              "&:hover": { backgroundColor: LegacyTokens.canvas, color: B13Ink.ink2 },
            }}
          >Cancel</Button>
          {/* Footer holds Preview and Cancel only. A real Save belongs
             here when in-place editing lands on this page. Until then,
             Edit is the header's sole affordance — a footer button
             labelled Save that does not save is worse than no button,
             which is the rule that governs whatever ships next. The
             prior "Edit"-labelled footer button was the placeholder for
             this slot; dropped once it collided with the header's Edit
             Program action. */}
        </Box>
      </Card>

      {/* Batch 43  - 4  -  session-time Preview. Read-only summary of the
         Program as a clinician sees it right before a session: name,
         objective, mastery workflow + prompt hierarchy names, and the
         list of active/acquisition/generalization targets with their SD.
         Skips administrative fields (folder, category, tags, history)
         and closed / discontinued targets to keep the page usable when
         the room is loud. */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${LegacyTokens.line}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: B13Ink.ink3 }}>
                Session-time preview
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: LegacyTokens.ink, mt: 0.5 }}>
                {program?.programName || "Program"}
              </Typography>
            </Box>
            {program?.status && <AbaStatusChip status={program.status} kind="program" size="medium" />}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {program?.objective && (
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.75 }}>Objective</Typography>
              <Typography sx={{ ...TypeScale.fieldValue }}>{program.objective}</Typography>
            </Box>
          )}
          <Grid container columnSpacing={4} rowSpacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>Workflow</Typography>
              <Typography sx={{ ...TypeScale.fieldValue }}>{program?.masteryWorkflowName || " - "}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>Prompt Levels</Typography>
              <Typography sx={{ ...TypeScale.fieldValue }}>{program?.promptTemplateName || " - "}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>Measurement</Typography>
              <Typography sx={{ ...TypeScale.fieldValue }}>{program?.measurementType || " - "}</Typography>
            </Grid>
          </Grid>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1 }}>
            Targets ({targets.filter((t) => !["closed", "discontinued", "hold"].includes(String(t.status || "").toLowerCase())).length})
          </Typography>
          <Stack spacing={1}>
            {targets
              .filter((t) => !["closed", "discontinued", "hold"].includes(String(t.status || "").toLowerCase()))
              .map((t) => (
                <Box key={t.id} sx={{
                  p: 1.5, border: `1px solid ${LegacyTokens.line}`,
                  borderRadius: `${LegacyTokens.radius.control}px`,
                  backgroundColor: LegacyTokens.canvas,
                }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink }}>{t.targetName}</Typography>
                      {t.sdText && (
                        <Typography sx={{ ...TypeScale.caption, mt: 0.5 }}>
                          <strong style={{ color: B13Ink.ink2 }}>SD:</strong> {t.sdText}
                          {t.sdType ? ` (${t.sdType})` : ""}
                        </Typography>
                      )}
                    </Box>
                    <AbaStatusChip status={t.status} />
                  </Stack>
                </Box>
              ))}
            {targets.length === 0 && (
              <Typography sx={{ ...TypeScale.caption }}>No targets on this program.</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} sx={{ color: B13Ink.ink3, textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Batch 44  -  the inline Import Targets dialog was replaced by the
         shared ImportTargetsDialog. Same paste-one-per-line flow, plus
         live duplicate warning against the current target list. */}
      <ImportTargetsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        programId={id ?? null}
        existingNames={targets.map((t) => String(t.targetName ?? ""))}
        onImported={() => refetchTargets()}
      />

      {/* Batch 10  - 10.3  -  Customize prompt levels modal, opened from the
         Overview panel next to the program's Prompt Levels field. Only
         mounts when a template is bound to the program (button doesn't
         render otherwise). */}
      {id && (
        <CustomizePromptLevelsModal
          open={customizePromptsOpen}
          programId={id}
          onClose={() => setCustomizePromptsOpen(false)}
        />
      )}

      {/* Batch 46  -  target row overflow menu. Six flat items, no inline
         expansion. The measurement submenu is a real flyout anchored to
         its parent MenuItem (right-side popper). "Change target type"
         is disabled at the top level with a tooltip  -  never opens.
         Placement flips above the row when the target sits low in the
         viewport (see openTargetMenu's spaceBelow check). */}
      <Menu
        anchorEl={targetMenuAnchor}
        open={Boolean(targetMenuAnchor)}
        onClose={closeTargetMenu}
        anchorOrigin={{ vertical: menuFlipUp ? "top" : "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: menuFlipUp ? "bottom" : "top", horizontal: "right" }}
        PaperProps={{ sx: {
          minWidth: 260,
          borderRadius: `${LegacyTokens.radius.control}px`,
          boxShadow: "0 4px 12px rgba(15, 61, 46, 0.10)",
        } }}
      >
        <MenuItem onClick={openMeasurementFlyout} sx={{ pr: 1 }}>
          <Box sx={{ flexGrow: 1, fontSize: 14 }}>Change measurement</Box>
          <Box sx={{ color: B13Ink.ink3 }}></Box>
        </MenuItem>

        <Tooltip
          title="Target types  -  Set of Targets, Shaping, Instructions  -  need sub-target and teaching-card support. Coming in a later release."
          placement="left"
          arrow
        >
          {/* Wrapper is required so the tooltip can attach to a disabled MenuItem. */}
          <Box>
            <MenuItem disabled sx={{ opacity: 0.55 }}>
              <Box sx={{ flexGrow: 1, fontSize: 14 }}>Change target type</Box>
              <Box sx={{ color: B13Ink.ink3 }}></Box>
            </MenuItem>
          </Box>
        </Tooltip>

        <Divider />

        <MenuItem
          onClick={() => { const row = targetMenuRow; closeTargetMenu(); if (row) handleOpenTargetSettings(row.id); }}
          sx={{ fontSize: 14 }}
        >
          Target Settings
        </MenuItem>
        <MenuItem
          onClick={() => { const row = targetMenuRow; closeTargetMenu(); if (row) handleDuplicateTarget(row.id); }}
          sx={{ fontSize: 14 }}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => { const row = targetMenuRow; closeTargetMenu(); if (row) handleDeleteTarget(row.id); }}
          sx={{ fontSize: 14, color: "error.main" }}
        >
          Delete
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => { const row = targetMenuRow; closeTargetMenu(); if (row) handleCopySupport(row); }}
          sx={{ fontSize: 14 }}
        >
          Copy support info to clipboard
        </MenuItem>
      </Menu>

      {/* Batch 46  -  measurement flyout. Seven plain items, no headers.
         Anchored to the right of the parent "Change measurement" MenuItem
         so it reads as a proper sub-menu. Selecting a value closes both
         menus and writes the override via the shared persistOverride path. */}
      <Menu
        anchorEl={measurementAnchor}
        open={Boolean(measurementAnchor)}
        onClose={closeMeasurementFlyout}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: {
          minWidth: 200,
          borderRadius: `${LegacyTokens.radius.control}px`,
          boxShadow: "0 4px 12px rgba(15, 61, 46, 0.10)",
        } }}
      >
        {["Percent Correct", "Frequency", "Duration", "Rate", "Latency", "Interval Recording", "Task Analysis"].map((m) => (
          <MenuItem
            key={m}
            selected={targetMenuRow?.measurementType === m}
            onClick={async () => {
              const row = targetMenuRow;
              closeMeasurementFlyout();
              closeTargetMenu();
              if (row) await persistOverride(row, { measurementType: m });
            }}
            sx={{ fontSize: 14 }}
          >
            {m}
          </MenuItem>
        ))}
      </Menu>

      {/* Batch 34  - 2.4  -  Bulk Set-SD dialog. Applies sdType + sdText to
         every currently-selected target in a single API call. */}
      <Dialog open={bulkSdOpen} onClose={() => setBulkSdOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set SD on {selectedTargets.length} target(s)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Categorise the SD and enter what the staff member says or presents.
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>SD Type</InputLabel>
              <Select label="SD Type" value={bulkSdType} onChange={(e) => setBulkSdType(String(e.target.value))}>
                {SD_TYPE_OPTIONS.filter((o) => o.value).map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField multiline minRows={3} fullWidth size="small"
              label="SD text" placeholder='e.g. "Try this" or "Solve using place value."'
              value={bulkSdText} onChange={(e) => setBulkSdText(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSdOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              const res = await updateTargetSD({
                targetIds: selectedTargets,
                sdType: bulkSdType || null,
                sdText: bulkSdText || null,
              }).unwrap();
              if (res.status) {
                showMessage(`SD applied to ${selectedTargets.length} target(s)`);
                setBulkSdOpen(false); setBulkSdText(""); refetchTargets();
                setSelectedTargets([]); setSelectAll(false);
              } else {
                showErrorMessage(res.message || "Failed to update SD");
              }
            } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to update SD"); }
          }}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Batch 32: Program Details tab panels ─────────────────────────
// Each reads from its dedicated endpoint added in Batch 32 and renders
// real content  -  no placeholders.

// Batch 36  - 1.1  -  real Session Data panel. Uses the already-installed
// highcharts + highcharts-react-official (see Dashboard/Component/payroll.tsx).
// If no rows exist yet, falls through to the shared empty-state pattern.
const ProgramSessionDataPanel: React.FC<{ programId: string | undefined; onRecord: () => void }> = ({ programId, onRecord }) => {
  const { data, isLoading } = useGetSessionDataQuery(
    programId ? { programId: Number(programId) } : {},
    { skip: !programId }
  );
  const rows: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];
  if (isLoading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (rows.length === 0) {
    return (
      <EmptyPanel
        title="No session data recorded yet."
        body="Record a session against this program to start populating the progress chart and history."
        actionLabel="Record Data"
        onAction={onRecord}
      />
    );
  }
  // Series: percentCorrect over sessionDate, per target.
  const byTarget: Record<string, Array<[number, number]>> = {};
  const sorted = [...rows].sort((a, b) => String(a.sessionDate).localeCompare(String(b.sessionDate)));
  for (const r of sorted) {
    const key = r.targetName || `Target ${r.targetId}`;
    const t = new Date(r.sessionDate).getTime();
    const y = Number(r.percentCorrect) || 0;
    if (!Number.isFinite(t)) continue;
    (byTarget[key] = byTarget[key] || []).push([t, y]);
  }
  // Batch 37  - 1.4 +  - 5.4  -  series palette, gridlines in --line at 1px,
  // axis labels in --ink-3, no gradients, lines 2.5px + markers 5px.
  // Legend top-right horizontal at 12px. Y axis 0-100 with % suffix,
  // four gridlines total. Chart height 320px.
  const options: Highcharts.Options = {
    chart: { type: "line", height: 320, style: { fontFamily: "inherit" }, backgroundColor: "transparent" },
    title: { text: undefined },
    credits: { enabled: false },
    colors: [...SeriesPalette],
    xAxis: {
      type: "datetime",
      lineColor: LegacyTokens.line,
      tickColor: LegacyTokens.line,
      labels: { style: { color: B13Ink.ink3, fontSize: "11px" } },
    },
    yAxis: {
      title: { text: undefined },
      min: 0, max: 100, tickAmount: 5,
      gridLineColor: LegacyTokens.line,
      gridLineWidth: 1,
      labels: {
        format: "{value}%",
        style: { color: B13Ink.ink3, fontSize: "11px" },
      },
    },
    tooltip: { shared: true, xDateFormat: "%b %e, %Y" },
    legend: {
      align: "right", verticalAlign: "top", layout: "horizontal",
      itemStyle: { fontSize: "12px", color: B13Ink.ink2, fontWeight: "500" },
      itemHoverStyle: { color: LegacyTokens.ink },
    },
    plotOptions: {
      line: {
        lineWidth: 2.5,
        marker: { enabled: true, radius: 5, symbol: "circle", lineWidth: 0 },
      },
    },
    series: Object.entries(byTarget).map(([name, data]) => ({ type: "line", name, data })),
  };
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Session Data</Typography>
        <Button variant="contained" size="small" onClick={onRecord}>Record Data</Button>
      </Stack>
      <Box sx={{ mb: 3 }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Target</strong></TableCell>
              <TableCell align="right"><strong>Trials</strong></TableCell>
              <TableCell align="right"><strong>Correct</strong></TableCell>
              <TableCell align="right"><strong>%</strong></TableCell>
              <TableCell><strong>Prompt</strong></TableCell>
              <TableCell><strong>Staff</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.slice().reverse().map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.sessionDate}</TableCell>
                <TableCell>{r.targetName}</TableCell>
                <TableCell align="right">{r.trialsCompleted ?? 0}</TableCell>
                <TableCell align="right">{r.correctTrials ?? 0}</TableCell>
                <TableCell align="right">{Math.round(Number(r.percentCorrect) || 0)}%</TableCell>
                <TableCell>{r.promptLevelUsed || " - "}</TableCell>
                <TableCell>{r.staffName || " - "}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ProgramStudentsPanel: React.FC<{
  programId: string | undefined;
  hierarchies: Array<{ id: number | string; templateName: string }>;
  workflows: Array<{ id: number | string; workflowName: string }>;
  targets: ITarget[];
}> = ({ programId, hierarchies, workflows, targets }) => {
  const { data, isLoading } = useGetProgramStudentsQuery(
    programId ? { programId: Number(programId) } : { skip: true },
    { skip: !programId }
  );
  const rows: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];

  // Dialog-based assignment. Replaces the previous
  // navigate("…/edit#students") — the destination did not consume the
  // fragment and dumped users at Section 1 of a nine-section editor.
  // The same dialog opens from both the empty state's "Assign Student"
  // button and the populated table's toolbar button, so there is one
  // path regardless of tab state.
  //
  // Save refetch: StudentsStaffWorkspace calls saveProgramAssignments
  // (invalidatesTags: ["program"]). The parent ProgramViewPage's
  // useGetProgramStudentsQuery (line 519, providesTags: ["program"])
  // feeds studentsCount into BOTH the "N STUDENTS" header figure and
  // the "Students N" tab label from a single source, so both refresh
  // off the same tag invalidation. This panel's own query refetches
  // the same way. No manual refetch call needed.
  const [assignOpen, setAssignOpen] = React.useState(false);
  // initialTargets: workspace expects id:number; local ITarget.id is
  // string — cast at the boundary rather than widen the workspace prop.
  const workspaceTargets = React.useMemo(
    () => targets.map((t) => ({
      id: Number(t.id),
      targetName: t.targetName,
      status: t.status,
      measurementType: t.measurementType ?? undefined,
    })),
    [targets]
  );

  const assignDialog = programId ? (
    <Dialog
      open={assignOpen}
      onClose={() => setAssignOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 2 }}>
        <Box sx={{ fontWeight: 700, fontSize: 18, flexGrow: 1 }}>Assign students</Box>
        <IconButton onClick={() => setAssignOpen(false)} aria-label="Close" sx={{ color: LegacyTokens.ink3 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <StudentsStaffWorkspace
          programId={Number(programId)}
          promptTemplates={hierarchies}
          workflows={workflows}
          initialTargets={workspaceTargets}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setAssignOpen(false)} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  ) : null;

  if (isLoading) return <Box p={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  if (rows.length === 0) {
    return (
      <>
        <EmptyPanel
          title="No students assigned yet."
          body="Assign a student so their sessions and progress land here."
          actionLabel="Assign Student"
          onAction={() => setAssignOpen(true)}
        />
        {assignDialog}
      </>
    );
  }
  // Batch 37  - 5.7  -  em-dash placeholders render in --ink-3 so they read as
  // "no data" rather than as real values. The cell falls back to the shared
  // Dash component; real values keep the standard tableCell colour.
  const Dash = () => <Box component="span" sx={{ color: B13Ink.ink3 }}> - </Box>;
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => setAssignOpen(true)}
          sx={{ backgroundColor: B13.primary, "&:hover": { backgroundColor: B13.primary } }}
        >
          Assign Student
        </Button>
      </Stack>
      <TableContainer component={Card} variant="outlined" sx={{
        borderColor: LegacyTokens.line, boxShadow: "none",
        borderRadius: `${LegacyTokens.radius.card}px`,
      }}>
        <Table size="small" sx={{ "& td, & th": { borderBottomColor: LegacyTokens.line } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: B13.canvas }}>
              <TableCell sx={{ ...TypeScale.tableHeader }}>Student</TableCell>
              <TableCell sx={{ ...TypeScale.tableHeader }}>Reference ID</TableCell>
              <TableCell sx={{ ...TypeScale.tableHeader }}>Location</TableCell>
              <TableCell sx={{ ...TypeScale.tableHeader }}>Last session</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s: any) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ ...TypeScale.tableCell }}>{s.name}</TableCell>
                <TableCell sx={{ ...TypeScale.tableCell }}>{s.reference_id || <Dash />}</TableCell>
                <TableCell sx={{ ...TypeScale.tableCell }}>{s.location_name || <Dash />}</TableCell>
                <TableCell sx={{ ...TypeScale.tableCell }}>{s.lastSession || <Dash />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {assignDialog}
    </Box>
  );
};

const ProgramProgressPanel: React.FC<{ programId: string | undefined }> = ({ programId }) => {
  const { data, isLoading } = useGetProgramProgressQuery(
    programId ? { programId: Number(programId), limit: 60 } : { skip: true },
    { skip: !programId }
  );
  if (isLoading) return <Box p={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  const d: any = data?.data || {};
  const series: Array<{ sessionDate: string; percentCorrect: number }> = Array.isArray(d.series) ? d.series : [];
  const phaseCounts: Record<string, number> = d.phaseCounts || {};
  const totalSessions: number = d.totalSessions ?? series.length;

  // Batch 37  - 5.5  -  full-width chart on the  - 1.4 palette. Same discipline as
  // the Session Data chart: seriesPalette, 1px gridlines in --line, ink-3
  // axis labels, 2.5px line, radius-5 markers, no gradient, legend hidden
  // (single series here).
  const chartOptions: Highcharts.Options = {
    chart: { type: "line", height: 320, style: { fontFamily: "inherit" }, backgroundColor: "transparent" },
    title: { text: undefined },
    credits: { enabled: false },
    colors: [...SeriesPalette],
    xAxis: {
      type: "datetime",
      lineColor: LegacyTokens.line, tickColor: LegacyTokens.line,
      labels: { style: { color: B13Ink.ink3, fontSize: "11px" } },
    },
    yAxis: {
      title: { text: undefined },
      min: 0, max: 100, tickAmount: 5,
      gridLineColor: LegacyTokens.line, gridLineWidth: 1,
      labels: { format: "{value}%", style: { color: B13Ink.ink3, fontSize: "11px" } },
    },
    tooltip: { xDateFormat: "%b %e, %Y" },
    legend: { enabled: false },
    plotOptions: {
      line: { lineWidth: 2.5, marker: { enabled: true, radius: 5, symbol: "circle", lineWidth: 0 } },
    },
    series: [{
      type: "line",
      name: "Percent correct",
      data: series.map((s) => [new Date(s.sessionDate).getTime(), Number(s.percentCorrect) || 0]),
    }],
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography sx={{ ...TypeScale.sectionHead, mb: 0.5 }}>Progress</Typography>
      <Typography sx={{ ...TypeScale.caption, mb: 3 }}>
        {totalSessions === 0
          ? "No session data recorded yet for this program (or any of its copies)."
          : `Percent correct over the last ${series.length} session(s).`}
      </Typography>

      {series.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Box>
      )}

      {/* Batch 37  - 5.5  -  "Targets by phase" chips on the shared  - 1.3
         status-triad treatment. Count sits next to the label in the chip's
         own text colour so it reads as part of the same object. */}
      <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1 }}>Targets by phase</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {Object.keys(phaseCounts).length === 0 ? (
          <Typography sx={{ ...TypeScale.caption }}>No targets</Typography>
        ) : Object.entries(phaseCounts).map(([phase, count]) => {
          const t = resolveStatusTriad(phase);
          return (
            <Box key={phase} component="span" sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75,
              backgroundColor: t.bg, color: t.text,
              borderRadius: `${LegacyTokens.radius.chip}px`,
              fontSize: 12, fontWeight: 600, px: 1, py: 0.25,
            }}>
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: t.dot }} />
              {t.label} <Box component="span" sx={{ fontWeight: 500, opacity: 0.75, ml: 0.25 }}>{count}</Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const ProgramHistoryPanel: React.FC<{ programId: string | undefined }> = ({ programId }) => {
  const { data, isLoading } = useGetProgramHistoryQuery(
    programId ? { programId: Number(programId) } : { skip: true },
    { skip: !programId }
  );
  const [targetsExpanded, setTargetsExpanded] = React.useState(false);
  if (isLoading) return <Box p={3} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;
  const events: Array<{ timestamp: string; kind: string; actor?: string | null; summary: string }> =
    Array.isArray(data?.data) ? (data!.data as any[]) : [];
  if (events.length === 0) {
    return <EmptyPanel title="No history events yet." body="Program lifecycle events (created, targets added, assignments) will appear here." />;
  }

  // Batch 37  - 5.6  -  group runs of identical "target_added" events under a
  // single collapsible row. Kind chips use the shared status treatment.
  type Grouped =
    | { kind: "single"; ev: typeof events[number] }
    | { kind: "targets_added"; count: number; timestamp: string; children: typeof events };
  const grouped: Grouped[] = [];
  let batch: typeof events = [];
  const flushBatch = () => {
    if (batch.length === 0) return;
    if (batch.length === 1) grouped.push({ kind: "single", ev: batch[0] });
    else grouped.push({ kind: "targets_added", count: batch.length, timestamp: batch[0].timestamp, children: batch });
    batch = [];
  };
  for (const e of events) {
    if (e.kind === "target_added") batch.push(e);
    else { flushBatch(); grouped.push({ kind: "single", ev: e }); }
  }
  flushBatch();

  // Batch 37 (c)  -  event kinds are NOT statuses; borrowing the status
  // triad palette implied a ranking that isn't there ("target added" in
  // mastery-green read as "mastered"). All kinds render neutral (canvas2
  // fill, ink3 text, no dot). The single exception is `created`  -  the one
  // origin event  -  which gets brand-soft fill + brand text. The chip
  // labels the row type, it does not rank it.
  const KindChip: React.FC<{ k: string }> = ({ k }) => {
    const isCreated = k === "created";
    return (
      <Box component="span" sx={{
        display: "inline-flex", alignItems: "center",
        backgroundColor: isCreated ? tint12(B13.primary) : B13.canvas,
        color:           isCreated ? B13.primary      : B13Ink.ink3,
        borderRadius: `${LegacyTokens.radius.chip}px`,
        fontSize: 12, fontWeight: 600, px: 1, py: 0.25,
      }}>
        {k.replace("_", " ")}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <TableContainer variant="outlined" component={Card} sx={{
        borderColor: LegacyTokens.line, boxShadow: "none",
        borderRadius: `${LegacyTokens.radius.card}px`,
      }}>
        <Table size="small" sx={{ "& td, & th": { borderBottomColor: LegacyTokens.line } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: B13.canvas }}>
              <TableCell sx={{ ...TypeScale.tableHeader, width: 200 }}>When</TableCell>
              <TableCell sx={{ ...TypeScale.tableHeader, width: 140 }}>Kind</TableCell>
              <TableCell sx={{ ...TypeScale.tableHeader }}>Summary</TableCell>
              <TableCell sx={{ ...TypeScale.tableHeader, width: 160 }}>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grouped.map((g, i) => {
              if (g.kind === "single") {
                return (
                  <TableRow key={i} hover>
                    <TableCell sx={{ ...TypeScale.tableCell }}>{g.ev.timestamp}</TableCell>
                    <TableCell><KindChip k={g.ev.kind} /></TableCell>
                    <TableCell sx={{ ...TypeScale.tableCell }}>{g.ev.summary}</TableCell>
                    <TableCell sx={{ ...TypeScale.tableCell, color: g.ev.actor ? B13Ink.ink2 : B13Ink.ink3 }}>{g.ev.actor || " - "}</TableCell>
                  </TableRow>
                );
              }
              return (
                <React.Fragment key={i}>
                  <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setTargetsExpanded((v) => !v)}>
                    <TableCell sx={{ ...TypeScale.tableCell }}>{g.timestamp}</TableCell>
                    <TableCell><KindChip k="target_added" /></TableCell>
                    <TableCell sx={{ ...TypeScale.tableCell }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {targetsExpanded
                          ? <ExpandMoreIcon fontSize="small" sx={{ color: B13Ink.ink3 }} />
                          : <ChevronRightIcon fontSize="small" sx={{ color: B13Ink.ink3 }} />}
                        {g.count} targets added
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink3 }}> - </TableCell>
                  </TableRow>
                  {targetsExpanded && g.children.map((c, j) => (
                    <TableRow key={`${i}-${j}`} sx={{ backgroundColor: LegacyTokens.canvas }}>
                      <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink3, pl: 6 }}>{c.timestamp}</TableCell>
                      <TableCell />
                      <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink3 }}>{c.summary}</TableCell>
                      <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink3 }}>{c.actor || " - "}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Drag-reorder scaffolding for the target list ────────────────
// Wraps the target-rows section with DndContext + SortableContext.
// The consumer passes children as a plain React node (the mapped
// row components). Rows are keyed by target.id, so no _dndId is
// needed — real DB id serves as the sortable identity, same pattern
// as CustomizePromptLevelsModal.
const SortableTargetsBody: React.FC<{
  targets: ITarget[];
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (e: DragEndEvent) => void;
  children: React.ReactNode;
}> = ({ targets, sensors, onDragEnd, children }) => (
  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
    <SortableContext items={targets.map((t) => String(t.id))} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  </DndContext>
);

// Per-row sortable wrapper. Uses a render-prop so the parent's rich
// JSX (which spans two TableRows — the header row + the conditional
// expanded panel — inside a Fragment) stays inline where it reads.
// The `handleRef` + `handleProps` land on the DragIndicatorIcon's
// container so drag is scoped to that single cell (not the whole
// row), and the transform is applied via a wrapper `<tbody>`-safe
// approach: the useSortable transform CSS is emitted on the header
// row via a style prop.
//
// Table-row visual idiom: no card elevation, subtle background shift
// + reduced opacity marks the row as in-flight without lifting it
// out of the table. isDragging is forwarded so the parent's TableRow
// sx can apply the shift.
// setNodeRef + transform style are handed to the parent so they can
// land directly on the header TableRow — wrapping in a <div> inside
// <tbody> would be invalid HTML. During drag only the header row
// transforms; the expanded panel row stays put, which is fine because
// dragging while expanded is not a supported flow.
const SortableTargetRow: React.FC<{
  id: string;
  triadDot: string;
  children: (bag: {
    handleProps: any;
    handleRef: (el: HTMLElement | null) => void;
    nodeRef: (el: HTMLElement | null) => void;
    rowStyle: React.CSSProperties;
    isDragging: boolean;
  }) => React.ReactNode;
}> = ({ id, children }) => {
  const {
    setNodeRef, setActivatorNodeRef, attributes, listeners,
    transform, transition, isDragging,
  } = useSortable({ id });
  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <>{children({
      handleProps: { ...attributes, ...listeners },
      handleRef: setActivatorNodeRef,
      nodeRef: setNodeRef,
      rowStyle,
      isDragging,
    })}</>
  );
};

export default Index;
